/**
 * NER example-span helpers for Generate-with-AI.
 * Entity shape matches the wire `NerEntity`: `{ type, start, end }` with exclusive end.
 */

const WORD_RE_SOURCE = "[A-Za-z0-9]+(?:['\\u2019-][A-Za-z0-9]+)*";

const wordRegex = () => new RegExp(WORD_RE_SOURCE, 'g');

export const overlaps = (left, right) => left.start < right.end && right.start < left.end;

export const hasOverlap = (entities, range, exceptIndex = -1) =>
    (entities || []).some((entity, index) => index !== exceptIndex && overlaps(entity, range));

export const findWordRanges = (text) => {
    const ranges = [];
    const source = text || '';
    const regex = wordRegex();
    let match = regex.exec(source);
    while (match) {
        ranges.push({ start: match.index, end: match.index + match[0].length });
        match = regex.exec(source);
    }
    return ranges;
};

export const trimRange = (text, start, end) => {
    const source = text || '';
    let nextStart = Math.max(0, start);
    let nextEnd = Math.min(source.length, end);
    while (nextStart < nextEnd && /\s/.test(source[nextStart])) nextStart += 1;
    while (nextEnd > nextStart && /\s/.test(source[nextEnd - 1])) nextEnd -= 1;
    return { start: nextStart, end: nextEnd };
};

export const addOrReplaceEntity = (entities, next) => {
    if (!next || next.start >= next.end) return entities || [];
    const current = entities || [];
    const sameRange = current.findIndex((entity) => entity.start === next.start && entity.end === next.end);
    if (sameRange >= 0) {
        return current.map((entity, index) =>
            index === sameRange ? { type: next.type, start: next.start, end: next.end } : entity
        );
    }
    if (hasOverlap(current, next)) return current;
    return [...current, { type: next.type, start: next.start, end: next.end }].sort(
        (left, right) => left.start - right.start || left.end - right.end
    );
};

export const removeEntityAt = (entities, start, end) =>
    (entities || []).filter((entity) => !(entity.start === start && entity.end === end));

export const stripEntitiesOfType = (entities, type) => (entities || []).filter((entity) => entity.type !== type);

export const entityForBackwardUntag = (entities, caret) =>
    (entities || []).find((entity) => entity.end === caret || (entity.start < caret && caret < entity.end));

export const entityForForwardUntag = (entities, caret) =>
    (entities || []).find((entity) => entity.start === caret || (entity.start < caret && caret < entity.end));

export const applyRangeReplace = (text, entities, start, end, insert = '') => {
    const from = Math.max(0, Math.min(start, end));
    const to = Math.max(from, Math.max(start, end));
    const source = text || '';
    const inserted = insert || '';
    const delta = inserted.length - (to - from);
    const nextText = `${source.slice(0, from)}${inserted}${source.slice(to)}`;
    const nextEntities = [];
    (entities || []).forEach((entity) => {
        if (entity.end <= from) {
            nextEntities.push(entity);
            return;
        }
        if (entity.start >= to) {
            nextEntities.push({ ...entity, start: entity.start + delta, end: entity.end + delta });
            return;
        }
        // Overlaps the replaced range — drop it.
    });
    return { text: nextText, entities: nextEntities };
};

export const applyInsert = (text, entities, caret, insert) => {
    const inserted = insert || '';
    const source = text || '';
    const nextText = `${source.slice(0, caret)}${inserted}${source.slice(caret)}`;
    const length = inserted.length;
    const nextEntities = (entities || []).map((entity) => {
        if (entity.start < caret && caret < entity.end) {
            return { ...entity, end: entity.end + length };
        }
        if (entity.start >= caret) {
            return { ...entity, start: entity.start + length, end: entity.end + length };
        }
        return entity;
    });
    return {
        text: nextText,
        entities: nextEntities,
        caret: caret + length,
        selectionStart: caret + length,
        selectionEnd: caret + length,
    };
};

const collapsedCaret = (caret) => ({
    caret,
    selectionStart: caret,
    selectionEnd: caret,
});

export const applyBackwardDelete = (text, entities, caret, selectionEnd = caret) => {
    const selStart = Math.min(caret, selectionEnd);
    const selEnd = Math.max(caret, selectionEnd);
    const source = text || '';
    const current = entities || [];

    if (selStart !== selEnd) {
        const exact = current.find((entity) => entity.start === selStart && entity.end === selEnd);
        if (exact) {
            return {
                text: source,
                entities: removeEntityAt(current, exact.start, exact.end),
                caret: selEnd,
                selectionStart: selStart,
                selectionEnd: selEnd,
            };
        }
        const replaced = applyRangeReplace(source, current, selStart, selEnd, '');
        return { ...replaced, ...collapsedCaret(selStart) };
    }

    const tagged = entityForBackwardUntag(current, caret);
    if (tagged) {
        return {
            text: source,
            entities: removeEntityAt(current, tagged.start, tagged.end),
            ...collapsedCaret(caret),
        };
    }

    if (caret <= 0) {
        return { text: source, entities: current, ...collapsedCaret(caret) };
    }

    const replaced = applyRangeReplace(source, current, caret - 1, caret, '');
    return { ...replaced, ...collapsedCaret(caret - 1) };
};

export const applyForwardDelete = (text, entities, caret, selectionEnd = caret) => {
    const selStart = Math.min(caret, selectionEnd);
    const selEnd = Math.max(caret, selectionEnd);
    if (selStart !== selEnd) {
        return applyBackwardDelete(text, entities, selStart, selEnd);
    }
    const source = text || '';
    const current = entities || [];
    const tagged = entityForForwardUntag(current, caret);
    if (tagged) {
        return {
            text: source,
            entities: removeEntityAt(current, tagged.start, tagged.end),
            ...collapsedCaret(caret),
        };
    }
    if (caret >= source.length) {
        return { text: source, entities: current, ...collapsedCaret(caret) };
    }
    const replaced = applyRangeReplace(source, current, caret, caret + 1, '');
    return { ...replaced, ...collapsedCaret(caret) };
};

export const toTrimmedNerExample = (text, entities) => {
    const source = text || '';
    if (!source.trim()) return null;
    const leading = source.match(/^\s*/)[0].length;
    const trimmed = source.trim();
    const nextEntities = (entities || [])
        .map((entity) => ({
            type: entity.type,
            start: entity.start - leading,
            end: entity.end - leading,
        }))
        .filter((entity) => entity.start >= 0 && entity.end <= trimmed.length && entity.start < entity.end);
    return { text: trimmed, entities: nextEntities };
};

export const buildRenderSegments = (text, entities) => {
    const source = text || '';
    const sorted = [...(entities || [])]
        .filter((entity) => entity.start < entity.end)
        .sort((left, right) => left.start - right.start || left.end - right.end);
    const segments = [];
    let last = 0;
    sorted.forEach((entity, index) => {
        const start = Math.max(0, Math.min(entity.start, source.length));
        const end = Math.max(start, Math.min(entity.end, source.length));
        if (start > last) {
            segments.push({ kind: 'text', start: last, end: start });
        }
        segments.push({ kind: 'entity', start, end, type: entity.type, index });
        last = end;
    });
    if (last < source.length) {
        segments.push({ kind: 'text', start: last, end: source.length });
    }
    return segments;
};

export const tokenizePlain = (text, from, to) => {
    const source = text || '';
    const start = Math.max(0, from);
    const end = Math.max(start, Math.min(to, source.length));
    const segment = source.slice(start, end);
    const tokens = [];
    const regex = wordRegex();
    let last = 0;
    let match = regex.exec(segment);
    while (match) {
        if (match.index > last) {
            tokens.push({ kind: 'text', start: start + last, end: start + match.index });
        }
        tokens.push({
            kind: 'word',
            start: start + match.index,
            end: start + match.index + match[0].length,
        });
        last = match.index + match[0].length;
        match = regex.exec(segment);
    }
    if (last < segment.length) {
        tokens.push({ kind: 'text', start: start + last, end: end });
    }
    return tokens;
};

const offsetInRoot = (root, container, nodeOffset) => {
    if (!root || !container) return 0;
    if (container.nodeType === Node.TEXT_NODE) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        let acc = 0;
        let node = walker.nextNode();
        while (node) {
            if (node === container) return acc + nodeOffset;
            acc += node.data.length;
            node = walker.nextNode();
        }
        return acc;
    }
    try {
        const prefix = document.createRange();
        prefix.selectNodeContents(root);
        prefix.setEnd(container, nodeOffset);
        return prefix.toString().length;
    } catch {
        return 0;
    }
};

export const caretOffsetIn = (root, which = 'start') => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !root) return 0;
    const range = selection.getRangeAt(0);
    const container = which === 'end' ? range.endContainer : range.startContainer;
    const nodeOffset = which === 'end' ? range.endOffset : range.startOffset;
    return offsetInRoot(root, container, nodeOffset);
};

export const setCaretIn = (root, start, end = start) => {
    if (!root) return;
    const selection = window.getSelection();
    if (!selection) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let acc = 0;
    let startNode = null;
    let startOffset = 0;
    let endNode = null;
    let endOffset = 0;
    let node = walker.nextNode();
    while (node) {
        const length = node.data.length;
        if (!startNode && acc + length >= start) {
            startNode = node;
            startOffset = start - acc;
        }
        if (!endNode && acc + length >= end) {
            endNode = node;
            endOffset = end - acc;
            break;
        }
        acc += length;
        node = walker.nextNode();
    }
    const range = document.createRange();
    if (!startNode) {
        range.selectNodeContents(root);
        range.collapse(start === 0);
    } else {
        const resolvedEnd = endNode || startNode;
        range.setStart(startNode, Math.max(0, Math.min(startOffset, startNode.data.length)));
        range.setEnd(resolvedEnd, Math.max(0, Math.min(endOffset, resolvedEnd.data.length)));
    }
    selection.removeAllRanges();
    selection.addRange(range);
};
